// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 5 miest
// CpKatalogView — Katalóg položiek CP (quote_catalog)
// Ústava sek. 66 — stĺpcová tabuľka, RowMenu, Export, multi-sort
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v4 (29. 9. 2026): rozpísaný formulár drží spoločnú stráž odchodu (ústava 53.1.b)
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useToast } from '../components/Toast.jsx'
import { useConfirm } from '../components/ConfirmDialog.jsx'
import { useBreadcrumb } from '../components/Layout.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { PageHeader, KpiRow, KpiCard, FilterChips, SearchInput, MasterDetail, EmptyPane, Tabs, ListStatusBar } from '../components/Ui.jsx'
import { BtnAdd, BtnGhost, Btn } from '../components/Btn.jsx'
import { Modal } from '../components/Modal.jsx'
import RowMenu from '../components/RowMenu.jsx'
import { useLock } from '../hooks/useLock.js'
import ExportMenu from '../components/ExportMenu.jsx'
import useSortable from '../hooks/useSortable.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { COLORS, TYPO, SPACE, RADIUS, SK, todayLocal } from '../theme.js'
import NavIcon from '../components/NavIcon.jsx'
import KatalogPolozkaModal, { TRADES } from '../components/KatalogPolozkaModal.jsx'   // cp_polozka_v1

// Trade kategórie — zhodné s VOP rozdelením
// cp_polozka_v1: TRADES aj dialog položky sú spoločné (components/KatalogPolozkaModal.jsx)
const TRADE_LABEL = Object.fromEntries(TRADES.map(t => [t.key, t.label]))
const TRADE_COLOR = Object.fromEntries(TRADES.map(t => [t.key, t.color]))

function noDiac(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export default function CpKatalogView() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const { setTrail } = useBreadcrumb()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [trade, setTrade] = useState('all')
  const [statusFilter, setStatusFilter] = useState('active')
  const [modal, setModal] = useState(null)  // {record} alebo {record: null}
  const [selectedId, setSelectedId] = useState(null)

  useEffect(() => {
    setTrail([{ label: 'Matrix v2' }, { label: 'Katalógy' }, { label: 'CP položky' }])
    return () => setTrail([])
  }, [setTrail])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetchAuth('/api/quote-catalog')
      const d = await r.json()
      setRows(Array.isArray(d) ? d : [])
    } catch (e) {
      toast.error('Chyba načítania: ' + e.message)
    } finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])
  useLiveRefresh(load)  // živé obnovenie (SSE, ústava 97)

  const filtered = useMemo(() => {
    let arr = rows
    if (statusFilter === 'active') arr = arr.filter(r => r.active !== 0)
    else if (statusFilter === 'archive') arr = arr.filter(r => r.active === 0)
    if (trade !== 'all') arr = arr.filter(r => (r.trade || r.category) === trade)
    if (search.trim()) {
      const q = noDiac(search)
      arr = arr.filter(r =>
        noDiac(r.title).includes(q) || noDiac(r.full_name).includes(q) ||
        noDiac(r.material).includes(q) || noDiac(r.id).includes(q)
      )
    }
    return arr
  }, [rows, statusFilter, trade, search])

  const sortable = useSortable(filtered)

  // ── klávesová navigácia zoznamu (ústava 54.2, 56.5) — zdieľaný hook ──
  const openRecord = useCallback((row) => setModal({ record: row }), [])
  useListKeyboard({
    items: sortable.sorted,
    selectedId,
    onSelect: setSelectedId,
    onOpen: openRecord,
    enabled: !modal,
  })

  // ── klávesové skratky (ústava sek. 54) ───────────────────────────
  useShortcuts([
    { keys: '/', label: 'Hľadať', scope: 'cpkatalog', handler: () => {
      const el = document.querySelector('[data-cpkatalog-search]')
      if (el) el.focus()
    } },
  ])

  const kpi = useMemo(() => ({
    total: rows.filter(r => r.active !== 0).length,
    omietky: rows.filter(r => r.active !== 0 && (r.trade || r.category) === 'omietky').length,
    potery: rows.filter(r => r.active !== 0 && (r.trade || r.category) === 'potery').length,
    archive: rows.filter(r => r.active === 0).length,
  }), [rows])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader title="Katalóg CP položiek" subtitle={`${filtered.length} z ${rows.length}`}>
        <ExportMenu
          data={sortable.sorted}
          filename={`katalog-cp-${todayLocal()}`}
          title="Katalóg CP položiek"
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'title', label: 'Názov' },
            { key: 'trade', label: 'Remeslo', value: r => TRADE_LABEL[r.trade || r.category] || r.trade },
            { key: 'unit', label: 'MJ' },
            { key: 'price', label: 'Cena' },
            { key: 'thickness', label: 'Hrúbka' },
            { key: 'consumption', label: 'Spotreba' },
            { key: 'material', label: 'Materiál' },
          ]}
        />
        <BtnAdd onClick={() => setModal({ record: null })}>+ Nová položka</BtnAdd>
      </PageHeader>

      <KpiRow cols={4}>
        <KpiCard title="Aktívne" color={COLORS.add} value={kpi.total} />
        <KpiCard title="Omietky" color="#22c55e" value={kpi.omietky} />
        <KpiCard title="Potery" color="#f97316" value={kpi.potery} />
        <KpiCard title="Archív" color={COLORS.muted} value={kpi.archive} />
      </KpiRow>

      <div style={{ height: SPACE.md }} />

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg,
        overflow: 'hidden',
      }}>
        <SearchInput value={search} onChange={setSearch} dataAttr="data-cpkatalog-search" placeholder="Hľadať (názov / materiál / ID)…" />
        <FilterChips
          options={[
            { key: 'active',  label: `Aktívne (${kpi.total})`,  color: COLORS.add },
            { key: 'all',     label: 'Všetky',                  color: COLORS.muted },
            { key: 'archive', label: `Archív (${kpi.archive})`, color: COLORS.muted },
          ]}
          value={statusFilter} onChange={setStatusFilter}
        />
        <div style={{ padding: '4px 10px', borderBottom: `1px solid ${COLORS.line}`,
          display: 'flex', alignItems: 'center', gap: 6, fontSize: TYPO.sizes.xs, color: COLORS.muted, flexWrap: 'wrap' }}>
          <span>Remeslo:</span>
          {[{ key: 'all', label: 'Všetky' }, ...TRADES].map(t => (
            <button key={t.key} onClick={() => setTrade(t.key)}
              style={{
                padding: '2px 10px',
                background: trade === t.key ? COLORS.navBg : 'transparent',
                border: `1px solid ${trade === t.key ? COLORS.brand : COLORS.line}`,
                borderRadius: RADIUS.full, cursor: 'pointer',
                fontSize: TYPO.sizes.xs, color: trade === t.key ? COLORS.brand : COLORS.muted,
                fontFamily: 'inherit',
              }}>{t.label}</button>
          ))}
        </div>

        {/* Tabuľkový layout — sticky header, zebra */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{
            position: 'sticky', top: 0, zIndex: 2,
            display: 'grid',
            gridTemplateColumns: '60px 80px 1fr 120px 80px 90px 100px 90px 36px',
            gap: 0, padding: '8px 12px',
            background: COLORS.bgSoft, borderBottom: `2px solid ${COLORS.line}`,
            fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
            color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            <div>Foto</div>
            <div onClick={sortable.sortClick('id')} style={{cursor:'pointer'}}>ID {sortable.sortIndicator('id')}</div>
            <div onClick={sortable.sortClick('title')} style={{cursor:'pointer'}}>Názov položky {sortable.sortIndicator('title')}</div>
            <div onClick={sortable.sortClick('trade')} style={{cursor:'pointer'}}>Remeslo {sortable.sortIndicator('trade')}</div>
            <div style={{ textAlign: 'center' }}>MJ</div>
            <div onClick={sortable.sortClick('price')} style={{textAlign:'right',cursor:'pointer'}}>Cena {sortable.sortIndicator('price')}</div>
            <div style={{ textAlign: 'center' }}>Hrúbka</div>
            <div style={{ textAlign: 'right' }}>Spotreba</div>
            <div></div>
          </div>

          {loading ? (
            <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>Načítavam…</div>
          ) : sortable.sorted.length === 0 ? (
            <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>
              Žiadne položky pre tento filter.
            </div>
          ) : sortable.sorted.map((row, idx) => {
            const isSelected = row.id === selectedId
            const t = row.trade || row.category || 'ine'
            const tColor = TRADE_COLOR[t] || COLORS.muted
            return (
              <div key={row.id}
                data-row-id={row.id}
                onClick={() => setSelectedId(row.id)}
                onDoubleClick={() => setModal({ record: row })}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 80px 1fr 120px 80px 90px 100px 90px 36px',
                  gap: 0, padding: '6px 12px',
                  borderBottom: `1px solid ${COLORS.line}`,
                  background: isSelected ? COLORS.navBg : (idx % 2 ? COLORS.bgSoft : 'transparent'),
                  borderLeft: `4px solid ${isSelected ? COLORS.brand : tColor}`,
                  cursor: 'pointer', fontSize: TYPO.sizes.sm, alignItems: 'center',
                  opacity: row.active === 0 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = COLORS.bgSoft }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = idx % 2 ? COLORS.bgSoft : 'transparent' }}
              >
                <div>
                  {row.image
                    ? <img src={`/uploads/${row.image}?t=${Date.now()}`} alt="" style={{
                        width: 44, height: 44, objectFit: 'cover',
                        borderRadius: RADIUS.sm, border: `1px solid ${COLORS.line}`,
                      }} />
                    : <div style={{
                        width: 44, height: 44, background: COLORS.bgSoft,
                        border: `1px dashed ${COLORS.line}`, borderRadius: RADIUS.sm,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, color: COLORS.muted,
                      }}><NavIcon name="fotky" size={18} /></div>}
                </div>
                <div style={{ fontFamily: TYPO.mono, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{row.id}</div>
                <div>
                  <div style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text,
                    whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.title || '(bez názvu)'}</div>
                  {row.material && (
                    <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontStyle: 'italic',
                      whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.material}</div>
                  )}
                </div>
                <div style={{ fontSize: TYPO.sizes.xs }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: RADIUS.full,
                    background: tColor + '20', color: tColor, fontWeight: TYPO.weights.semibold,
                  }}>{TRADE_LABEL[t] || t}</span>
                </div>
                <div style={{ textAlign: 'center', fontFamily: TYPO.mono, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                  {row.unit || 'm²'}
                </div>
                <div style={{ textAlign: 'right', fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>
                  {SK.eur(row.price || 0)}
                </div>
                <div style={{ textAlign: 'center', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                  {row.thickness || '—'}
                </div>
                <div style={{ textAlign: 'right', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                  {row.consumption || '—'}
                </div>
                <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  <RowMenu compact actions={[
                    { key: 'edit',  label: 'Upraviť',         onClick: () => setModal({ record: row }) },
                    { key: 'copy',  label: 'Vytvoriť kópiu',  onClick: async () => {
                      try {
                        const r = await fetchAuth('/api/quote-catalog', {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...row, title: row.title + ' (kópia)' }),
                        })
                        if (!r.ok) throw new Error('chyba')
                        toast.success('Kópia vytvorená')
                        load()
                      } catch (e) { toast.error('Chyba: ' + e.message) }
                    } },
                    { separator: true },
                    { key: 'archive', label: row.active === 0 ? 'Obnoviť' : 'Archivovať',
                      icon: row.active === 0 ? '↩' : '', danger: row.active !== 0,
                      onClick: async () => {
                        const isArchive = row.active !== 0
                        const ok = await confirm({
                          title: isArchive ? 'Archivovať položku?' : 'Obnoviť položku?',
                          message: `${row.title || row.id} sa ${isArchive ? 'presunie do archívu' : 'vráti medzi aktívne'}.`,
                          danger: isArchive,
                          confirmLabel: isArchive ? 'Archivovať' : 'Obnoviť',
                        })
                        if (!ok) return
                        try {
                          if (isArchive) {
                            await fetchAuth(`/api/quote-catalog/${row.id}`, { method: 'DELETE' })
                          } else {
                            await fetchAuth(`/api/quote-catalog/${row.id}`, {
                              method: 'PUT', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ ...row, active: 1 }),
                            })
                          }
                          toast.success(isArchive ? 'Archivované' : 'Obnovené')
                          load()
                        } catch (e) { toast.error('Chyba: ' + e.message) }
                      } },
                  ]} />
                </div>
              </div>
            )
          })}
        </div>

        <ListStatusBar>
          {filtered.length} z {rows.length} položiek
          {filtered.length > 0 && ` · spolu ${SK.eur(filtered.reduce((s, r) => s + (Number(r.price) || 0), 0))}`}
        </ListStatusBar>
      </div>

      {modal && (
        <KatalogPolozkaModal record={modal.record}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
        />
      )}
    </div>
  )
}

